import { useContext, useMemo } from "react";
import { ModuleBuilderContext } from "@src/features/libraryItem/module/contexts/ModuleBuilderContext";
import { SetActiveClientInput } from "@src/types.generated";
import { ClientIdContext } from "@src/features/builders/shared/presence/ClientIdContext";
import { CourseBuilderContext } from "@src/features/libraryItem/course/contexts/CourseBuilderContext";
import useSkillBuilderStore, {
  useActiveSkillBuilderStepId,
} from "@src/features/libraryItem/skill/hooks/useSkillBuilderStore";
import { isEqual } from "lodash";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";

import useSyncActiveClientCore from "@src/features/builders/shared/presence/useSyncActiveClientCore";

import { moduleVersionIdAtom } from "../module/atoms";
import { courseVersionIdAtom, undefinedAtom } from "../course/atoms";
import { richTextDocumentIdAtom } from "../resource/atoms";

type Opts = { skip?: boolean };

// LibraryItem wrapper: assembles the active-client input from the
// builder contexts (course / module / skill / training resource)
// + the active prompt / membership / step. Delegates the
// mutation/interval/unload orchestration to useSyncActiveClientCore.
const useSyncActiveClient = (opts?: Opts): void => {
  const { skip } = opts || {};
  const courseBuilderContext = useContext(CourseBuilderContext);
  const moduleBuilderContext = useContext(ModuleBuilderContext);
  const skillVersionId = useSkillBuilderStore(
    (state) => state.skillVersion?.id,
  );
  const activeStepId = useActiveSkillBuilderStepId();
  const clientIdContext = useContext(ClientIdContext);

  const promptUuidAtom = useMemo(
    () =>
      selectAtom(
        courseBuilderContext?.activeScreenAtom ?? undefinedAtom,
        (activeScreen) => {
          if (activeScreen && "promptScreen" in activeScreen) {
            return activeScreen.promptScreen.prompt.uuid;
          }
          return null;
        },
        isEqual,
      ),
    [courseBuilderContext?.activeScreenAtom],
  );
  const promptUuid = useAtomValue(promptUuidAtom);
  const courseVersionId = useAtomValue(courseVersionIdAtom);
  const moduleVersionId = useAtomValue(moduleVersionIdAtom);
  const richTextDocumentId = useAtomValue(richTextDocumentIdAtom);

  const input = useMemo<SetActiveClientInput | null>(() => {
    if (skip || !clientIdContext) return null;
    return {
      clientId: clientIdContext.clientId,
      promptUuid,
      courseVersionId: courseVersionId || null,
      moduleVersionId: moduleVersionId || null,
      moduleContentMembershipUuid:
        moduleBuilderContext?.activeMembershipUuid || null,
      skillVersionId: skillVersionId || null,
      verificationStepId: activeStepId,
      richTextDocumentId: richTextDocumentId,
    } satisfies SetActiveClientInput;
  }, [
    skip,
    clientIdContext,
    promptUuid,
    courseVersionId,
    moduleVersionId,
    moduleBuilderContext?.activeMembershipUuid,
    skillVersionId,
    activeStepId,
    richTextDocumentId,
  ]);

  useSyncActiveClientCore(input);
};

export default useSyncActiveClient;
